import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function AdminRecover() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !newPassword.trim() || !token.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/admin-recover", {
        email: email.trim(),
        new_password: newPassword,
        recovery_token: token.trim(),
      });
      toast.success(data.message || "Password reset.");
      setTimeout(() => navigate("/login", { replace: true }), 1200);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F4F4F4] text-black flex items-center justify-center px-5 py-10" data-testid="admin-recover-page">
      <div className="w-full max-w-lg bg-white border-2 border-black brutal-shadow p-8">
        <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-[#E00000]" style={{ fontWeight: 900 }}>
          Owner Account Recovery
        </div>
        <h1 className="mt-2 font-serif-display text-4xl leading-tight" style={{ fontWeight: 800 }}>
          Reset Admin Password
        </h1>
        <p className="mt-3 text-sm" style={{ fontWeight: 600 }}>
          Use this page only when you're locked out of the owner account. Requires the
          server-side recovery token (set in <code className="font-mono-civic">backend/.env</code> as
          <code className="font-mono-civic"> ADMIN_RECOVERY_TOKEN</code>). The email you enter must already
          be on the admin allow-list.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-recover-form">
          <div>
            <label className="block font-mono-civic text-xs uppercase tracking-widest mb-1" style={{ fontWeight: 900 }}>
              Admin Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              className="w-full border-2 border-black p-2.5 font-mono-civic text-sm focus:outline-none focus:bg-[#FFF8DC]"
              style={{ fontWeight: 600 }}
              data-testid="admin-recover-email"
            />
          </div>
          <div>
            <label className="block font-mono-civic text-xs uppercase tracking-widest mb-1" style={{ fontWeight: 900 }}>
              New Password (min 6 chars)
            </label>
            <input
              type="password"
              required
              minLength={6}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full border-2 border-black p-2.5 font-mono-civic text-sm focus:outline-none focus:bg-[#FFF8DC]"
              style={{ fontWeight: 600 }}
              data-testid="admin-recover-password"
            />
          </div>
          <div>
            <label className="block font-mono-civic text-xs uppercase tracking-widest mb-1" style={{ fontWeight: 900 }}>
              Recovery Token
            </label>
            <input
              type="text"
              required
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="w-full border-2 border-black p-2.5 font-mono-civic text-sm focus:outline-none focus:bg-[#FFF8DC]"
              style={{ fontWeight: 600 }}
              data-testid="admin-recover-token"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#E00000] text-white uppercase tracking-widest py-3 border-2 border-[#E00000] disabled:opacity-40"
            style={{ fontWeight: 900 }}
            data-testid="admin-recover-submit"
          >
            {busy ? "Resetting…" : "Reset Password →"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="underline text-sm" style={{ fontWeight: 800 }}>
            ← Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
